/**
 * Adapted from react-native-picker-select and @react-native-picker/picker.
 * These libs weren't compatible with vite, so we had to create our own
 * implementation for web. We're still using the libs on native (see:
 * PickerSelect.native.tsx).
 */
import { forwardRef, useState } from 'react';

import { View } from 'react-native';
import type { ViewProps } from 'react-native';
import type { PickerSelectProps as RNPickerSelectProps } from 'react-native-picker-select';

type Item = Pick<RNPickerSelectProps['items'][0], 'key' | 'label'> & {
  /**
   * The value of the item
   */
  value: string;
};

export type PickerSelectProps = Omit<
  RNPickerSelectProps,
  'value' | 'items' | 'placeholder' | 'onValueChange'
> & {
  /**
   * The currently selected value
   */
  value: string;
  /**
   * The items to display in the picker
   */
  items: Array<Item>;
  /**
   * The placeholder item to display in the picker
   */
  placeholder: Item;
  /**
   * Callback called when the selected value changes
   */
  onValueChange: (value: string) => void;
};

const getSelectedItem = ({
  items,
  key,
  value,
}: Pick<PickerSelectProps, 'items'> & Pick<Item, 'key' | 'value'>) => {
  let idx = items.findIndex((item) => {
    if (item.key && key) {
      return item.key === key;
    }
    return item.value === value;
  });
  if (idx === -1) {
    idx = 0;
  }
  return items[idx] || {};
};

/**
 * Renders <select> on web
 */
export const PickerSelect = ({
  disabled,
  style,
  pickerProps,
  Icon,
  items,
  placeholder,
  itemKey,
  value,
  onValueChange,
}: PickerSelectProps) => {
  const allItems = [placeholder].concat(items);

  const [selectedItem, setSelectedItem] = useState(
    getSelectedItem({ items: allItems, key: itemKey, value }),
  );

  const handleValueChange = (value: string) => {
    const selectedItem = getSelectedItem({ items: allItems, value });
    setSelectedItem(selectedItem);
    onValueChange(selectedItem.value);
  };

  return (
    <View
      style={[
        {
          alignSelf: 'stretch',
        },
        style?.viewContainer,
      ]}
    >
      <Picker
        style={style?.inputWeb}
        enabled={!disabled}
        onValueChange={handleValueChange}
        selectedValue={selectedItem.value}
        {...pickerProps}
      >
        {allItems.map((item) => (
          <option key={item.key} value={item.value} label={item.label}>
            {item.label}
          </option>
        ))}
      </Picker>
      <IconComponent Icon={Icon} style={style} />
    </View>
  );
};

const IconComponent = ({ Icon, style }: Pick<PickerSelectProps, 'Icon' | 'style'>) => {
  if (!Icon) {
    return null;
  }

  return (
    <View
      style={[
        {
          position: 'absolute',
          right: 0,
        },
        style?.iconContainer,
      ]}
    >
      <Icon />
    </View>
  );
};

type PickerProps = ViewProps & {
  /**
   * Whether the picker should be enabled
   */
  enabled?: boolean;
  /**
   * Handler called when the selected value changes
   */
  onValueChange?: (arg0: string, arg1: number) => void;
  /**
   * The value of the currently selected item
   */
  selectedValue?: number | string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Select = forwardRef(function Select(props: any, forwardedRef) {
  return <select {...props} ref={forwardedRef} />;
});

const Picker = forwardRef(function Picker(
  { enabled, onValueChange, selectedValue, children, ...other }: PickerProps,
  forwardedRef,
) {
  const handleChange: JSX.IntrinsicElements['select']['onChange'] = (e) => {
    const { selectedIndex, value } = e.target;
    if (onValueChange) {
      onValueChange(value, selectedIndex);
    }
  };

  return (
    <Select
      disabled={enabled === false ? true : undefined}
      onChange={handleChange}
      ref={forwardedRef}
      value={selectedValue}
      {...other}
    >
      {children}
    </Select>
  );
});
